// pkg125 — admin prop: Client Modules workspace renders this page with adminApi I/O; Submit hidden in admin mode (D-124e) — delivered 2026-07-27
// pkg23 — "Divorce Certificate" rename + conditional race/ethnicity "specify" inputs the PH-1682 needs — delivered 2026-06-12
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi'; // pkg125 — admin mode
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const ETHNICITY_OPTIONS = [
  'Not Spanish/Hispanic/Latino',
  'Yes, Mexican/Mexican American',
  'Yes, Puerto Rican',
  'Yes, Cuban',
  'Yes, other Spanish/Hispanic/Latino',
  'Unknown',
];

const RACE_OPTIONS = [
  'White',
  'Black or African American',
  'American Indian or Alaskan Native',
  'Asian Indian',
  'Chinese',
  'Filipino',
  'Japanese',
  'Korean',
  'Vietnamese',
  'Native Hawaiian',
  'Guamanian or Chamorro',
  'Samoan',
  'Other Pacific Islander',
  'Other',
  'Unknown',
];

const EDUCATION_OPTIONS = [
  '8th grade or less',
  '9th–12th grade, no diploma',
  'High school graduate or GED completed',
  'Some college credit but no degree',
  'Associate degree (e.g., AA, AS)',
  "Bachelor's degree (e.g., BA, BS)",
  "Master's degree (e.g., MA, MS, MBA)",
  'Doctorate or Professional degree (e.g., PhD, MD, JD)',
  'Unknown',
];

const MARRIAGE_NUM = ['1st Marriage','2nd Marriage','3rd Marriage','4th Marriage','5th Marriage'];
const PREV_MARRIAGE_END = ['Not Applicable','Divorce','Death','Annulment'];

const EMPTY = {
  // Petitioner/Plaintiff
  plaintiff_name: '', plaintiff_name_prior: '', plaintiff_ssn: '',
  plaintiff_state: '', plaintiff_county: '', plaintiff_city: '', plaintiff_address: '',
  plaintiff_birthplace: '', plaintiff_dob: '',
  plaintiff_ethnicity: '', plaintiff_race: '', plaintiff_education: '',
  plaintiff_marriage_number: '', plaintiff_prev_marriage_end: '',
  // pkg23 — conditional "specify" fields the PH-1682 requires (petitioner)
  plaintiff_ethnicity_other: '',
  plaintiff_race_tribe: '', plaintiff_race_other_asian: '',
  plaintiff_race_other_pacific: '', plaintiff_race_other: '',
  // Respondent/Defendant
  defendant_name: '', defendant_name_prior: '', defendant_ssn: '',
  defendant_state: '', defendant_county: '', defendant_city: '', defendant_address: '',
  defendant_birthplace: '', defendant_dob: '',
  defendant_ethnicity: '', defendant_race: '', defendant_education: '',
  defendant_marriage_number: '', defendant_prev_marriage_end: '',
  // pkg23 — conditional "specify" fields the PH-1682 requires (respondent)
  defendant_ethnicity_other: '',
  defendant_race_tribe: '', defendant_race_other_asian: '',
  defendant_race_other_pacific: '', defendant_race_other: '',
  // Marriage info
  marriage_state: '', marriage_county: '', marriage_date: '',
  last_resided_together: '',
  // Children
  children_born_alive: '', children_under_18: '',
  custody_plaintiff: '', custody_defendant: '', custody_joint: '', custody_other: '',
  children_list: '',
  // Decree
  joint_petition: 'No',
  name_restored_plaintiff: 'No', name_restored_plaintiff_specify: '',
  name_restored_defendant: 'No', name_restored_defendant_specify: '',
};

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

// pkg125 — `admin` prop ({ clientId }): rendered by the Client Modules
// workspace with adminApi I/O; Submit hidden (D-124e). The client's
// submitted banner still shows so the firm sees their status.
export default function DemographicPage({ admin = null }) {
  const { firm } = useFirmSettings();
  const io = admin ? {
    get:  () => adminApi.getClientDemographic(admin.clientId),
    save: (data) => adminApi.saveClientDemographic(admin.clientId, data),
  } : {
    get:  () => api.getDemographic(),
    save: (data) => api.saveDemographic(data),
  };
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    io.get().then(rec => {
      if (rec?.data) setForm({ ...EMPTY, ...rec.data });
      if (rec?.submitted_at) setSubmitted(true);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function input(key, placeholder, type = 'text') {
    return (
      <input
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={placeholder || ''}
      />
    );
  }

  function select(key, options, placeholder = '— Select —') {
    return (
      <select value={form[key]} onChange={e => set(key, e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  async function handleSave() {
    setSaving(true); setStatus(''); setError('');
    try {
      await io.save(form);
      setStatus('Progress saved.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleSubmit() {
    setSubmitting(true); setStatus(''); setError('');
    try {
      await api.submitDemographic(form);
      setSubmitted(true);
      setStatus('Information submitted successfully. Your attorney has been notified.');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Divorce Certificate</h1>
      <p className="page-subtitle">
        This information completes your Tennessee Certificate of Divorce or Annulment (PH-1682),
        required for your divorce filing with the Tennessee Department of Health.
        Fill in as much as you can — your attorney will review everything before filing.
        Your Social Security Number is required by the court and is transmitted securely.
      </p>

      {submitted && (
        <div className="alert alert-success">
          ✓ This form has been submitted to your attorney. You may still update and resubmit if anything changes.
        </div>
      )}
      {status && <div className="alert alert-success">{status}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* PETITIONER / PLAINTIFF */}
      <div className="section-head">Petitioner / Plaintiff Information</div>
      <div className="card">
        <div className="form-row">
          <Field label="Full Legal Name (First, Middle, Last)">{input('plaintiff_name', 'e.g. Jane Marie Smith')}</Field>
          <Field label="Name Prior to First Marriage">{input('plaintiff_name_prior', 'Maiden or previous name')}</Field>
        </div>
        <div className="form-row three">
          <Field label="Social Security Number">{input('plaintiff_ssn', 'XXX-XX-XXXX')}</Field>
          <Field label="Date of Birth">{input('plaintiff_dob', 'MM/DD/YYYY', 'date')}</Field>
          <Field label="State of Birth">{input('plaintiff_birthplace', 'e.g. Tennessee')}</Field>
        </div>
        <div className="form-row three">
          <Field label="Residence — State">{input('plaintiff_state', 'e.g. Tennessee')}</Field>
          <Field label="County">{input('plaintiff_county', 'e.g. Hamilton')}</Field>
          <Field label="City / Town">{input('plaintiff_city', 'e.g. Chattanooga')}</Field>
        </div>
        <Field label="Street Address">{input('plaintiff_address', '123 Main Street')}</Field>
        <div className="form-row">
          <Field label="Hispanic/Latino Origin">{select('plaintiff_ethnicity', ETHNICITY_OPTIONS)}</Field>
          <Field label="Race">{select('plaintiff_race', RACE_OPTIONS)}</Field>
        </div>
        {/* pkg23 — conditional "specify" inputs the certificate needs */}
        {form.plaintiff_ethnicity === 'Yes, other Spanish/Hispanic/Latino' && (
          <Field label="Specify Hispanic/Latino origin">{input('plaintiff_ethnicity_other', '')}</Field>
        )}
        {form.plaintiff_race === 'American Indian or Alaskan Native' && (
          <Field label="Name of enrolled or principal tribe">{input('plaintiff_race_tribe', '')}</Field>
        )}
        {form.plaintiff_race === 'Other Asian' && (
          <Field label="Specify (other Asian)">{input('plaintiff_race_other_asian', '')}</Field>
        )}
        {form.plaintiff_race === 'Other Pacific Islander' && (
          <Field label="Specify (other Pacific Islander)">{input('plaintiff_race_other_pacific', '')}</Field>
        )}
        {form.plaintiff_race === 'Other' && (
          <Field label="Specify (other race)">{input('plaintiff_race_other', '')}</Field>
        )}
        <div className="form-row">
          <Field label="Highest Level of Education">{select('plaintiff_education', EDUCATION_OPTIONS)}</Field>
          <Field label="Number of This Marriage">{select('plaintiff_marriage_number', MARRIAGE_NUM)}</Field>
        </div>
        <Field label="If Not First Marriage, How Did Last Marriage End?">
          {select('plaintiff_prev_marriage_end', PREV_MARRIAGE_END)}
        </Field>
      </div>

      {/* RESPONDENT / DEFENDANT */}
      <div className="section-head">Respondent / Defendant Information</div>
      <div className="card">
        <div className="form-row">
          <Field label="Full Legal Name (First, Middle, Last)">{input('defendant_name', 'e.g. John Robert Smith')}</Field>
          <Field label="Name Prior to First Marriage">{input('defendant_name_prior', 'Maiden or previous name')}</Field>
        </div>
        <div className="form-row three">
          <Field label="Social Security Number">{input('defendant_ssn', 'XXX-XX-XXXX')}</Field>
          <Field label="Date of Birth">{input('defendant_dob', 'MM/DD/YYYY', 'date')}</Field>
          <Field label="State of Birth">{input('defendant_birthplace', 'e.g. Georgia')}</Field>
        </div>
        <div className="form-row three">
          <Field label="Residence — State">{input('defendant_state', '')}</Field>
          <Field label="County">{input('defendant_county', '')}</Field>
          <Field label="City / Town">{input('defendant_city', '')}</Field>
        </div>
        <Field label="Street Address">{input('defendant_address', '')}</Field>
        <div className="form-row">
          <Field label="Hispanic/Latino Origin">{select('defendant_ethnicity', ETHNICITY_OPTIONS)}</Field>
          <Field label="Race">{select('defendant_race', RACE_OPTIONS)}</Field>
        </div>
        {/* pkg23 — conditional "specify" inputs the certificate needs */}
        {form.defendant_ethnicity === 'Yes, other Spanish/Hispanic/Latino' && (
          <Field label="Specify Hispanic/Latino origin">{input('defendant_ethnicity_other', '')}</Field>
        )}
        {form.defendant_race === 'American Indian or Alaskan Native' && (
          <Field label="Name of enrolled or principal tribe">{input('defendant_race_tribe', '')}</Field>
        )}
        {form.defendant_race === 'Other Asian' && (
          <Field label="Specify (other Asian)">{input('defendant_race_other_asian', '')}</Field>
        )}
        {form.defendant_race === 'Other Pacific Islander' && (
          <Field label="Specify (other Pacific Islander)">{input('defendant_race_other_pacific', '')}</Field>
        )}
        {form.defendant_race === 'Other' && (
          <Field label="Specify (other race)">{input('defendant_race_other', '')}</Field>
        )}
        <div className="form-row">
          <Field label="Highest Level of Education">{select('defendant_education', EDUCATION_OPTIONS)}</Field>
          <Field label="Number of This Marriage">{select('defendant_marriage_number', MARRIAGE_NUM)}</Field>
        </div>
        <Field label="If Not First Marriage, How Did Last Marriage End?">
          {select('defendant_prev_marriage_end', PREV_MARRIAGE_END)}
        </Field>
      </div>

      {/* MARRIAGE */}
      <div className="section-head">Marriage Information</div>
      <div className="card">
        <div className="form-row">
          <Field label="State of Marriage">{input('marriage_state', 'e.g. Tennessee')}</Field>
          <Field label="County of Marriage">{input('marriage_county', 'e.g. Hamilton')}</Field>
        </div>
        <div className="form-row">
          <Field label="Date of Marriage">{input('marriage_date', 'MM/DD/YYYY', 'date')}</Field>
          <Field label="Date Couple Last Resided Together">{input('last_resided_together', 'MM/DD/YYYY', 'date')}</Field>
        </div>
        <div className="form-row">
          <Field label="Joint Petition for Divorce?">
            <select value={form.joint_petition} onChange={e => set('joint_petition', e.target.value)}>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </Field>
        </div>
      </div>

      {/* CHILDREN */}
      <div className="section-head">Children</div>
      <div className="card">
        <div className="form-row">
          <Field label="Number of Children Ever Born Alive of This Marriage">{input('children_born_alive', '0')}</Field>
          <Field label="Number of Children Under 18 in This Family">{input('children_under_18', '0')}</Field>
        </div>
        <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 12 }}>
          Number of children under 18 whose physical custody was awarded to:
        </p>
        <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          <Field label="Petitioner/Plaintiff">{input('custody_plaintiff', '0')}</Field>
          <Field label="Respondent/Defendant">{input('custody_defendant', '0')}</Field>
          <Field label="Joint">{input('custody_joint', '0')}</Field>
          <Field label="Other">{input('custody_other', '0')}</Field>
        </div>
        <Field label="List all children of this marriage with dates of birth">
          <textarea
            value={form.children_list}
            onChange={e => set('children_list', e.target.value)}
            placeholder="Example:&#10;Emily Jane Smith — 03/15/2018&#10;Jacob Ryan Smith — 07/22/2020"
            style={{ minHeight: 100 }}
          />
        </Field>
      </div>

      {/* NAME RESTORATION */}
      <div className="section-head">Name Restoration</div>
      <div className="card">
        <div className="form-row">
          <Field label="Was Petitioner's Name Prior to First Marriage or Previous Legal Surname Restored?">
            <select value={form.name_restored_plaintiff} onChange={e => set('name_restored_plaintiff', e.target.value)}>
              <option value="No">No</option>
              <option value="Yes">Yes — specify below</option>
            </select>
          </Field>
          {form.name_restored_plaintiff === 'Yes — specify below' && (
            <Field label="Specify Restored Name">{input('name_restored_plaintiff_specify', '')}</Field>
          )}
        </div>
        <div className="form-row">
          <Field label="Was Respondent's Name Prior to First Marriage or Previous Legal Surname Restored?">
            <select value={form.name_restored_defendant} onChange={e => set('name_restored_defendant', e.target.value)}>
              <option value="No">No</option>
              <option value="Yes">Yes — specify below</option>
            </select>
          </Field>
          {form.name_restored_defendant === 'Yes — specify below' && (
            <Field label="Specify Restored Name">{input('name_restored_defendant_specify', '')}</Field>
          )}
        </div>
      </div>

      <div className="btn-row">
        <button className="btn" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner" />Saving...</> : 'Save Progress'}
        </button>
        {!admin && (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner" />Submitting...</> : 'Submit to Attorney'}
          </button>
        )}
      </div>
      {!admin && (
        <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 12 }}>
          Submitting will send your information to {firm.client_contact_email || 'the firm'}. You can update and resubmit at any time.
        </p>
      )}
    </div>
  );
}
